import { useCallback, useEffect, useState } from 'react';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { pmGet, pmPost } from '../api';
import './ActionRegister.css';
import { SelectField, PROMPT, UNSET } from '@platform/ui-kit';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Action Register.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/action-register.ejs`.
 *
 * WHAT IT DOES, in the source's words: a single, accountable home for every action,
 * follow-up, decision, question, dependency, escalation and approval across a project —
 * tracked at project level or against a specific opportunity, each with an owner, dates
 * and a clear status.
 *
 * TWO TABS: Register (the list) and Dashboard (KPIs and charts), the same two the source
 * has, with the same counts on the Register tab's badge.
 *
 * THE DASHBOARD IS CSS, NOT CANVAS. The source loads Chart.js from a CDN. This platform
 * carries no charting dependency and `portfolio_definition/projects/Dashboards.tsx`
 * already established the precedent — reproduce the source's charts as CSS bars with the
 * SAME measures, groupings and orderings. Every number below, every bucket boundary and
 * every colour is the source's; only the drawing is different. Logged in
 * `docs/DEVIATIONS.md`.
 *
 * THE DRAWER ALWAYS OPENS IN EDIT MODE (§13.4). It used to open read-only for an existing
 * action and editable for a new one; clicking a row means you intend to change it, so the
 * extra Edit press bought nothing. The Edit button is removed rather than hidden — with
 * `editing` permanently true it would have nothing to do.
 *
 * CANCEL RESTORES A SNAPSHOT AND CLOSES. The snapshot is taken when the drawer opens, so
 * abandoning an edit puts every field back rather than re-fetching; closing is what makes it
 * legible, because there is no longer a read-only state for it to drop back into.
 *
 * `alert()` becomes an inline line, as elsewhere in this module; `confirm()` before delete
 * is kept, and Escape still closes the drawer.
 */

interface ProjectRow {
  project_id: string;
  project_description?: string | null;
}

interface OppRow {
  opportunity_id: string;
  description?: string | null;
}

interface TypeRow {
  name: string;
}

interface ActionRow {
  id: number;
  project_id?: string | null;
  opportunity_id?: string | null;
  title?: string | null;
  action_type?: string | null;
  category?: string | null;
  description?: string | null;
  workstream?: string | null;
  phase?: string | null;
  sprint?: string | null;
  owner?: string | null;
  assigned_by?: string | null;
  supporting_team?: string | null;
  stakeholders?: string | null;
  created_date?: string | null;
  due_date?: string | null;
  followup_date?: string | null;
  closed_date?: string | null;
  status?: string | null;
  waiting_on?: string | null;
  priority?: string | null;
  percent_complete?: number | null;
  estimated_effort?: string | null;
  actual_effort?: string | null;
  comments?: string | null;
  attachments?: string | null;
  meeting_minutes_link?: string | null;
  external_links?: string | null;
}

/** Every field the drawer edits, as the source's `FIELD_IDS`. */
interface Draft {
  id: string;
  /* §5.3 — the action's own project, editable in the drawer. Required; the save endpoint
     validates it independently, because the drawer is not the enforcement point. */
  project_id: string;
  title: string;
  action_type: string;
  category: string;
  description: string;
  opportunity_id: string;
  workstream: string;
  phase: string;
  sprint: string;
  owner: string;
  assigned_by: string;
  supporting_team: string;
  stakeholders: string;
  created_date: string;
  due_date: string;
  followup_date: string;
  closed_date: string;
  status: string;
  waiting_on: string;
  priority: string;
  percent_complete: string;
  estimated_effort: string;
  actual_effort: string;
  comments: string;
  attachments: string;
  meeting_minutes_link: string;
  external_links: string;
}

/* The source's own constants, verbatim. */
const STATUS_LIST = ['Not Started', 'In Progress', 'Waiting', 'Blocked', 'Completed', 'Cancelled'];
const PRIORITY_LIST = ['Critical', 'High', 'Medium', 'Low'];
const STATUS_COLORS: Record<string, string> = {
  'Not Started': '#90a4ae',
  'In Progress': '#1976d2',
  Waiting: '#ef6c00',
  Blocked: '#c62828',
  Completed: '#2e7d32',
  Cancelled: '#b0bec5',
};
const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#c62828',
  High: '#ef6c00',
  Medium: '#1976d2',
  Low: '#78909c',
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const isOpen = (a: ActionRow) => a.status !== 'Completed' && a.status !== 'Cancelled';
const daysBetween = (a: string, b: string) =>
  Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
const fmtDate = (d?: string | null) => (d ? String(d).slice(0, 10) : '—');
const dOrEmpty = (d?: string | null) => (d ? String(d).slice(0, 10) : '');
/** `chipStyle(color)` — a 1f fill, the colour itself, and a 55 border. */
const chipStyle = (color: string) => ({
  background: `${color}1f`,
  color,
  border: `1px solid ${color}55`,
});

const EMPTY_DRAFT: Draft = {
  id: '',
  project_id: '',
  title: '',
  action_type: '',
  category: '',
  description: '',
  opportunity_id: '',
  workstream: '',
  phase: '',
  sprint: '',
  owner: '',
  assigned_by: '',
  supporting_team: '',
  stakeholders: '',
  created_date: '',
  due_date: '',
  followup_date: '',
  closed_date: '',
  status: 'Not Started',
  waiting_on: '',
  priority: 'Medium',
  percent_complete: '0',
  estimated_effort: '',
  actual_effort: '',
  comments: '',
  attachments: '',
  meeting_minutes_link: '',
  external_links: '',
};

/**
 * §5.1 — Follow up on an action.
 *
 * Named **Follow up** for the same reason it is on Portfolio Exceptions: the register already
 * speaks this language. *Follow-up* is one of its ten action types, `followup_date` is the
 * field that drives chasing, and the subject below reads `Follow-up:`. When a label and the
 * schema disagree, the label is usually the thing that is wrong.
 *
 * The register stores an owner NAME, so the address comes from the directory built server-side
 * from work-item assignees. Where the name does not resolve the mail STILL OPENS, with the
 * recipient blank — a Follow up that silently does nothing would be worse than one you have to
 * address yourself.
 */
function followUpHref(a: ActionRow, directory: Record<string, string>): string {
  const email = directory[String(a.owner ?? '').trim().toLowerCase()] ?? '';
  const where = a.project_id + (a.opportunity_id ? ` / ${a.opportunity_id}` : '');
  const due = a.due_date ? String(a.due_date).slice(0, 10) : '';
  const state = !due
    ? 'has no due date'
    : due < new Date().toISOString().slice(0, 10)
      ? `was due ${due}`
      : `is due ${due}`;
  const body =
    `Hi ${String(a.owner ?? '').split(' ')[0]},\n\n` +
    `Checking in on "${a.title}" (${where}).\n` +
    `It ${state} and is recorded at ${a.percent_complete ?? 0}% complete, status ${a.status ?? '—'}.\n\n` +
    `Could you let me know where it stands, and whether the date still holds?\n\nThanks`;
  return (
    `mailto:${encodeURIComponent(email)}` +
    `?subject=${encodeURIComponent(`Follow-up: ${a.title} — ${where}`)}` +
    `&body=${encodeURIComponent(body)}`
  );
}

/*
 * "How this works" — cloned from the source's `arRegisterHelp` / `arDashboardHelp`
 * (`views/action-register.ejs:3-55`), TABBED, one pane per screen tab.
 *
 * The Status & priority section carries §18.3's rule verbatim, and that is the point of adding
 * it in the same change: an automatic status change that is not written down reads as the app
 * losing the user's input.
 */
const AR_HELPS: HelpContent[] = [
  {
    tabLabel: 'Register',
    title: 'Action Register',
    icon: '🗂️',
    purpose:
      'A single, accountable home for every <strong>action, follow-up, decision, question, dependency, escalation and approval</strong> across a project. Track items at the <strong>project level</strong> or against a specific <strong>opportunity</strong> &mdash; each with an owner, dates and a clear status.',
    doHere: [
      'Pick a <strong>Project</strong>, and optionally an <strong>Opportunity</strong> (or keep it project-level). The register is always read one project at a time.',
      'Click <strong>+ New action</strong>, choose its <strong>Project</strong> and <strong>Type</strong>, give it a title, owner and due date.',
      "Click <strong>Follow up</strong> on any row to open a pre-written email to that action's owner &mdash; the same follow-up you get from Portfolio Exceptions.",
      'Click any row to open its <strong>detail panel</strong> and change any field.',
      'Set <strong>% Complete</strong> and the status follows it &mdash; 0% Not Started, 1&ndash;99% In Progress, 100% Completed (the close date is stamped automatically). Set <strong>Waiting</strong>, <strong>Blocked</strong> or <strong>Cancelled</strong> by hand &mdash; those three are never overwritten by the percentage.',
    ],
    fits: 'Project Management &rarr; Delivery Execution &rarr; <strong>Action Register</strong> &rarr; Execution Control Center',
    firstTime: 'Select a project, click + New action, fill Type / Title / Owner / Due date, and Save.',
    sections: [
      {
        label: 'Following up with an owner',
        type: 'html',
        html: "<strong>Follow up</strong> opens your mail client with the message already written &mdash; the action, its due date, its status and percent complete, and what it is waiting on. The address comes from the people assigned to that project's work items; where an owner has no email on file the message still opens with the recipient left blank.<br><br>Nothing is sent by the app. Sending stays with you, and you record the outcome back on the action.",
      },
      {
        label: 'Record types',
        type: 'defs',
        items: [
          { term: 'Action Item', color: '#3949ab', def: 'A task assigned to an individual or team.' },
          { term: 'Follow-up', color: '#00897b', def: 'Verify or check the status of an activity.' },
          { term: 'Decision', color: '#8e24aa', def: 'An item awaiting direction (Decision Required) or a documented approved one (Decision Made).' },
          { term: 'Dependency', color: '#ef6c00', def: 'Waiting on another team or project.' },
          { term: 'Escalation', color: '#c62828', def: 'Item requiring management attention.' },
        ],
        note: 'These ten types are the complete list and cannot be extended. A fixed vocabulary is what lets <strong>Meeting Intelligence</strong> classify actions reliably from a transcript, and keeps the register comparable across every project.',
      },
      {
        label: 'Status & priority',
        type: 'html',
        html: 'Status runs <strong>Not Started &rarr; In Progress &rarr; Waiting / Blocked &rarr; Completed / Cancelled</strong>. When Status is <strong>Waiting</strong>, set <strong>Waiting on</strong> (Customer / Vendor / Internal) so the dashboard can count blockers. Priority is Critical / High / Medium / Low.<br><br><strong>% Complete drives the status.</strong> <strong>0%</strong> sets <strong>Not Started</strong>, <strong>1&ndash;99%</strong> sets <strong>In Progress</strong>, and <strong>100%</strong> sets <strong>Completed</strong> &mdash; so the status can never be left saying something the number contradicts. <strong>Waiting</strong>, <strong>Blocked</strong> and <strong>Cancelled</strong> are never overwritten &mdash; each says something the percentage cannot. An action can be 40% done <em>and</em> blocked, so the number is not allowed to erase that; to release it, change the status by hand and the percentage takes over again.',
      },
    ],
    links: [
      { label: '→ Execution Control Center', href: '/project_management/program-management/delivery-execution/execution-control-center' },
    ],
  },
  {
    tabLabel: 'Dashboard',
    title: 'Action Register — Dashboard',
    icon: '📊',
    purpose:
      "A live read of the register you're viewing: <strong>open, overdue, due-this-week, high-priority, closed-this-month</strong> and <strong>average days to close</strong>, plus how work is distributed by owner, status, priority, workstream and age.",
    doHere: [
      'Read the <strong>KPI tiles</strong> for the health of the current project/opportunity scope.',
      'Scan the <strong>charts</strong> to spot an overloaded owner, a pile-up in one status, or aging items.',
      'Switch back to the <strong>Register</strong> tab to act on anything that stands out.',
    ],
    fits: 'Register &rarr; <strong>Dashboard</strong> (same data, aggregated live)',
    firstTime: 'Pick a project on the Register tab, then open Dashboard to see the roll-up.',
    sections: [
      {
        label: 'What the tiles mean',
        type: 'defs',
        items: [
          { term: 'Open', color: '#3949ab', def: 'Anything not Completed or Cancelled.' },
          { term: 'Overdue', color: '#c62828', def: 'Open items whose due date has passed.' },
          { term: 'Due this week', color: '#ef6c00', def: 'Open items due within the next 7 days.' },
          { term: 'Avg days to close', color: '#00897b', def: 'Mean of (closed date − created date) across closed items.' },
        ],
      },
    ],
    links: [
      { label: '→ Execution Control Center', href: '/project_management/program-management/delivery-execution/execution-control-center' },
    ],
  },
];

/**
 * §18.3 — the status a percentage implies. A pure function, and exported, so the rule has ONE
 * definition that the screen uses and the tests exercise.
 *
 * Percent complete drives the status, so it cannot go stale by being left un-set:
 *   0% -> Not Started,  1-99% -> In Progress,  100% -> Completed
 *
 * THE HOLD LIST IS THE DESIGN, NOT A DETAIL. A derived field may only overwrite the states it
 * can actually infer. A percentage can infer *not started*, *underway* and *finished*; it cannot
 * infer *waiting on someone*, *blocked* or *cancelled*, because those are orthogonal to progress.
 * An action can be 40% done AND blocked, and moving it back to In Progress because someone
 * nudged the number would erase the only record that it is stuck. So those three are held, and
 * the user releases one by changing the status by hand — after which the number takes over again.
 *
 * 'In Progress' is the spelling in STATUS_LIST. Assigning any other one — 'In-Process', say —
 * sets the select to an option that does not exist, which silently blanks the field on save and
 * drops the row out of every chart and filter that matches on the status vocabulary.
 *
 * A blank box is NOT zero, and neither is 'abc': clearing the field to retype must not slam the
 * status to Not Started between keystrokes, and parseInt('abc') is NaN, which without the guard
 * would fall through the comparison chain and land on In Progress.
 */
const PCT_HOLDS = ['Waiting', 'Blocked', 'Cancelled'];

export function nextStatusForPercent(value: string, status: string): string {
  if (value === '' || value === null || value === undefined) return status;
  const pct = parseInt(value, 10);
  if (isNaN(pct)) return status;
  if (PCT_HOLDS.indexOf(status) >= 0) return status;
  return pct >= 100 ? 'Completed' : pct <= 0 ? 'Not Started' : 'In Progress';
}

function countBy<T>(arr: T[], keyFn: (x: T) => string): Record<string, number> {
  const m: Record<string, number> = {};
  arr.forEach((a) => {
    const k = keyFn(a);
    m[k] = (m[k] || 0) + 1;
  });
  return m;
}

/** The source's bar chart, as CSS. Value above the bar becomes value beside it. */
function BarChart({ data, color }: { data: Record<string, number>; color: string }) {
  const labels = Object.keys(data);
  if (!labels.length) return <div className="ar-empty">—</div>;
  const max = Math.max(1, ...labels.map((l) => data[l] ?? 0));
  return (
    <div>
      {labels.map((l) => (
        <div className="cbar-row" key={l}>
          <div className="cbar-lbl" title={l}>
            {l}
          </div>
          <div className="cbar-track">
            <div
              className="cbar-fill"
              style={{ width: `${((data[l] ?? 0) / max) * 100}%`, background: color }}
            />
          </div>
          <div className="cbar-val">{data[l]}</div>
        </div>
      ))}
    </div>
  );
}

/** The source's doughnut, as a proportional strip plus its legend. Same slices, same counts. */
function Doughnut({ slices }: { slices: { k: string; v: number; c: string }[] }) {
  if (!slices.length) return <div className="ar-empty">—</div>;
  const total = slices.reduce((s, x) => s + x.v, 0) || 1;
  return (
    <div>
      <div className="cdough-strip">
        {slices.map((s) => (
          <div
            className="cdough-seg"
            key={s.k}
            style={{ width: `${(s.v / total) * 100}%`, background: s.c }}
            title={`${s.k}: ${s.v}`}
          />
        ))}
      </div>
      <div className="cdough-legend">
        {slices.map((s) => (
          <span className="cdough-key" key={s.k}>
            <span className="cdough-box" style={{ background: s.c }} />
            {s.k} <span className="cdough-n">{s.v}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function ActionRegister() {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [opps, setOpps] = useState<OppRow[]>([]);
  const [types, setTypes] = useState<TypeRow[]>([]);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [projectId, setProjectId] = useState('');
  const [oppId, setOppId] = useState(UNSET);
  /*
   * §13.5 — THERE IS NO CROSS-PROJECT MODE, and its removal is not a simplification.
   *
   * An earlier version of this screen offered "All open projects". It was removed because of
   * what it did in use: work is done ONE PROJECT AT A TIME, and a screen spanning several of
   * them makes it far too easy to edit something on a project you were not looking at. That is
   * not hypothetical — work items on a second programme were edited from a cross-project screen
   * without the user realising.
   *
   * Portfolio Exceptions remains portfolio-wide, because that IS its purpose — but it is built
   * so it cannot damage anything by accident. This screen edits, so it stays scoped.
   *
   * Do not reintroduce `__all__` here. The server's `OPEN_PROJECT` predicate stays: it is what
   * keeps closed projects out of the drawer's own project picker (§5.3).
   */
  /*
   * §5.1 — name → email, for Follow up. The register stores an owner NAME, so without this
   * the button has nothing to address. Built server-side from the work items' assignee JSON:
   * the same people, already recorded. Keyed lower-case because the owner field is free text.
   */
  const [directory, setDirectory] = useState<Record<string, string>>({});
  const [openOnly, setOpenOnly] = useState(true);
  const [tab, setTab] = useState<'register' | 'dashboard'>('register');
  const [notice, setNotice] = useState<string | null>(null);

  /* Drawer */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [snapshot, setSnapshot] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const loadTypes = useCallback(async () => {
    try {
      const d = await pmGet<{ types: TypeRow[] }>('/action-register/types');
      setTypes(d.types ?? []);
    } catch {
      /* the source logs and carries on */
    }
  }, []);

  useEffect(() => {
    void loadTypes();
    void (async () => {
      try {
        const d = await pmGet<{ projects: ProjectRow[] }>('/action-register/projects');
        setProjects(d.projects ?? []);
      } catch {
        /* the source logs and carries on */
      }
    })();
  }, [loadTypes]);

  const loadActions = useCallback(async (pid: string, oppRaw: string) => {
    if (!pid) return;
    /* UNSET is "nothing chosen yet" and '' is "All (project-wide)" — both mean no
       opportunity filter, so the request is identical to before. */
    const opp = oppRaw === UNSET ? '' : oppRaw;
    let url = `/action-register/list?project_id=${encodeURIComponent(pid)}`;
    if (opp) url += `&opportunity_id=${encodeURIComponent(opp)}`;
    try {
      const d = await pmGet<{ actions: ActionRow[] }>(url);
      setActions(d.actions ?? []);
    } catch {
      /* the source logs and carries on */
    }
  }, []);

  const onProject = async (pid: string) => {
    setProjectId(pid);
    setOppId('');
    if (!pid) {
      setOpps([]);
      setActions([]);
      setDirectory({});
      return;
    }
    // Addresses for Follow up. Failing to resolve one is not fatal — the mail still opens.
    pmGet<{ directory: Record<string, string> }>(
      `/action-register/owner-directory?project_id=${encodeURIComponent(pid)}`,
    )
      .then((d) => setDirectory(d.directory ?? {}))
      .catch(() => setDirectory({}));
    try {
      const d = await pmGet<{ opportunities: OppRow[] }>(
        `/action-register/opportunities?project_id=${encodeURIComponent(pid)}`,
      );
      setOpps(d.opportunities ?? []);
    } catch {
      /* the source logs and carries on */
    }
    void loadActions(pid, '');
  };

  /* Escape closes the drawer, as the source's keydown listener does. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /*
   * §5.3 — the drawer's own opportunity list, which is NOT the toolbar's.
   *
   * The toolbar's list belongs to the toolbar's project. Once the drawer can change project,
   * the two diverge, and reusing the toolbar's list would offer initiatives from a different
   * project — letting an action be attached to one it does not belong to.
   */
  const [drawerOpps, setDrawerOpps] = useState<OppRow[]>([]);
  /* The project picker inside the drawer: open projects, plus the one an action being edited
     already belongs to even if that project has since closed. */
  const [drawerProjects, setDrawerProjects] = useState<ProjectRow[]>([]);

  const loadDrawerScope = useCallback(async (pid: string) => {
    if (!pid || pid === '__all__') {
      setDrawerOpps([]);
      return;
    }
    try {
      const d = await pmGet<{ opportunities: OppRow[] }>(
        `/action-register/opportunities?project_id=${encodeURIComponent(pid)}`,
      );
      setDrawerOpps(d.opportunities ?? []);
    } catch {
      setDrawerOpps([]);
    }
  }, []);

  /*
   * ASYNC, because it may fetch the opportunity list for a project other than the toolbar's.
   * Every call site awaits it or fires it with `void`; there are two, both below.
   */
  const openDrawer = async (a: ActionRow | null) => {
    const next: Draft = a
      ? {
          id: String(a.id),
          project_id: a.project_id || '',
          title: a.title || '',
          action_type: a.action_type || '',
          category: a.category || '',
          description: a.description || '',
          opportunity_id: a.opportunity_id || '',
          workstream: a.workstream || '',
          phase: a.phase || '',
          sprint: a.sprint || '',
          owner: a.owner || '',
          assigned_by: a.assigned_by || '',
          supporting_team: a.supporting_team || '',
          stakeholders: a.stakeholders || '',
          created_date: dOrEmpty(a.created_date),
          due_date: dOrEmpty(a.due_date),
          followup_date: dOrEmpty(a.followup_date),
          closed_date: dOrEmpty(a.closed_date),
          status: a.status || 'Not Started',
          waiting_on: a.waiting_on || '',
          priority: a.priority || 'Medium',
          percent_complete: String(a.percent_complete ?? 0),
          estimated_effort: a.estimated_effort || '',
          actual_effort: a.actual_effort || '',
          comments: a.comments || '',
          attachments: a.attachments || '',
          meeting_minutes_link: a.meeting_minutes_link || '',
          external_links: a.external_links || '',
        }
      : {
          ...EMPTY_DRAFT,
          created_date: todayISO(),
          /* Pre-filled with the selected project; BLANK in portfolio mode, where there is no
             single project to assume. §5.3. */
          project_id: projectId,
          // A scope already chosen in the toolbar pre-selects, as the source does.
          opportunity_id: oppId && oppId !== '__project__' && oppId !== UNSET ? oppId : '',
        };
    /* The drawer's own lists, for the project this action actually belongs to. `include=`
       keeps a since-closed project in the picker so an old action stays openable. */
    const pid = next.project_id || projectId;
    try {
      const d = await pmGet<{ projects: ProjectRow[] }>(
        `/action-register/projects?include=${encodeURIComponent(pid)}`,
      );
      setDrawerProjects(d.projects ?? []);
    } catch {
      setDrawerProjects(projects);
    }
    await loadDrawerScope(pid);

    setDraft(next);
    setNotice(null);
    /* §13.4 — always editable, and ALWAYS snapshot: Cancel restores this exact state, and
       previously only a new action had one to restore. */
    setEditing(true);
    setSnapshot(next);
    setDrawerOpen(true);
  };

  const set = (k: keyof Draft, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  /*
   * §18.3 — LET THE NUMBER SET THE STATUS.
   *
   * A status that has to be changed by hand is a status that goes stale. The percentage already
   * says where the work is, so the dropdown follows it — see nextStatusForPercent for the rule
   * and for why Waiting, Blocked and Cancelled are held.
   *
   * React's onChange fires per keystroke on a controlled input, which is the `oninput` timing
   * the source wires this to — it reacts as the number is typed rather than on blur.
   */
  const onPercentChange = (v: string) =>
    setDraft((d) => ({ ...d, percent_complete: v, status: nextStatusForPercent(v, d.status) }));

  /*
   * §5.4 — THERE IS NO "add new type". Ten types, and the list is closed.
   *
   * The affordance and the `addType` call that backed it are both gone, and the server now
   * answers `POST /action-register/types` with 403 regardless — hiding a control is not a
   * constraint, and the browser is not the enforcement point.
   *
   * A fixed vocabulary is what lets Meeting Intelligence classify transcript proposals
   * reliably, and what keeps registers comparable between projects.
   */

  const saveAction = async () => {
    const title = draft.title.trim();
    if (!title) {
      setNotice('Title is required.');
      return;
    }
    /*
     * §5.3 — the action's OWN project, not the toolbar's.
     *
     * This used to send `projectId`, which is the toolbar selection. In portfolio mode that
     * is the literal `__all__`, so every action created from the portfolio view would have
     * been written against a project id that does not exist. The drawer's field is the
     * authority now, and the toolbar only pre-fills it.
     */
    if (!draft.project_id) {
      setNotice('Project is required.');
      return;
    }
    const scope = draft.opportunity_id;
    const payload = {
      id: draft.id || null,
      project_id: draft.project_id,
      opportunity_id: scope === '__project__' || scope === '' ? null : scope,
      title,
      /* The `__add__` sentinel is gone with the "add new type" option it belonged to (§5.4).
         Guarding against a value the picker can no longer produce implied a feature that no
         longer exists. */
      action_type: draft.action_type,
      category: draft.category,
      description: draft.description,
      workstream: draft.workstream,
      phase: draft.phase,
      sprint: draft.sprint,
      owner: draft.owner,
      assigned_by: draft.assigned_by,
      supporting_team: draft.supporting_team,
      stakeholders: draft.stakeholders,
      created_date: draft.created_date,
      due_date: draft.due_date,
      followup_date: draft.followup_date,
      closed_date: draft.closed_date,
      status: draft.status,
      waiting_on: draft.waiting_on,
      priority: draft.priority,
      percent_complete: draft.percent_complete,
      estimated_effort: draft.estimated_effort,
      actual_effort: draft.actual_effort,
      comments: draft.comments,
      attachments: draft.attachments,
      meeting_minutes_link: draft.meeting_minutes_link,
      external_links: draft.external_links,
    };
    setSaving(true);
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/action-register/save',
        payload,
      );
      if (!d.success) {
        setNotice(`Error: ${d.error || 'failed'}`);
        setSaving(false);
        return;
      }
      setSaving(false);
      setDrawerOpen(false);
      await loadActions(projectId, oppId);
    } catch {
      setNotice('Request failed');
      setSaving(false);
    }
  };

  const deleteAction = async () => {
    if (!draft.id) return;
    if (!window.confirm('Delete this action? It will be removed from the register.')) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/action-register/delete', {
        id: draft.id,
      });
      if (!d.success) {
        setNotice(`Error: ${d.error || 'failed'}`);
        return;
      }
      setDrawerOpen(false);
      await loadActions(projectId, oppId);
    } catch {
      setNotice('Request failed');
    }
  };

  /* ── Register list ────────────────────────────────────────────────────── */

  const list = openOnly ? actions.filter(isOpen) : actions;
  const scopeLabel = (a: ActionRow) => a.opportunity_id || 'Project-level';

  /* ── Dashboard, every measure the source's ───────────────────────────── */

  const t = todayISO();
  const open = actions.filter(isOpen);
  const overdue = open.filter((a) => a.due_date && a.due_date.slice(0, 10) < t);
  const weekEnd = new Date();
  weekEnd.setDate(weekEnd.getDate() + 7);
  const weekISO = weekEnd.toISOString().slice(0, 10);
  const dueWeek = open.filter(
    (a) => a.due_date && a.due_date.slice(0, 10) >= t && a.due_date.slice(0, 10) <= weekISO,
  );
  const highPri = open.filter((a) => a.priority === 'Critical' || a.priority === 'High');
  /*
   * §5.5 — the two "Waiting on customer/vendor" KPI cards were REMOVED, and the counts behind
   * them with them.
   *
   * They read `waiting_on = 'Customer' | 'Vendor'` — a field set by hand in a dropdown that
   * only appears when Status is Waiting, and it was null on every row in the source install.
   * So both cards read zero permanently. A metric nothing populates is worse than no metric:
   * it occupies the most valuable space on the screen while teaching the reader to ignore it.
   *
   * The FIELD stays. It is still written by the drawer, still cleared when the status moves
   * off Waiting, and still shown beside the status chip on the row — only the two dead cards
   * are gone. Removing the field would be removing something nobody named.
   */
  const monPrefix = t.slice(0, 7);
  const closedMonth = actions.filter((a) => a.closed_date && a.closed_date.slice(0, 7) === monPrefix);
  const closedAll = actions.filter((a) => a.closed_date && a.created_date);
  const avgClose = closedAll.length
    ? Math.round(
        closedAll.reduce(
          (s, a) => s + Math.max(0, daysBetween(a.created_date!, a.closed_date!)),
          0,
        ) / closedAll.length,
      )
    : 0;

  const byOwner = countBy(open, (a) => a.owner || 'Unassigned');
  const byWs = countBy(open, (a) => a.workstream || 'Unassigned');
  const byStatus = STATUS_LIST.map((s) => ({
    k: s,
    v: actions.filter((a) => a.status === s).length,
    c: STATUS_COLORS[s]!,
  })).filter((x) => x.v > 0);
  const byPri = PRIORITY_LIST.map((s) => ({
    k: s,
    v: actions.filter((a) => a.priority === s).length,
    c: PRIORITY_COLORS[s]!,
  })).filter((x) => x.v > 0);
  const buckets: Record<string, number> = { '0–7d': 0, '8–14d': 0, '15–30d': 0, '30d+': 0 };
  open.forEach((a) => {
    const d = a.created_date ? daysBetween(a.created_date, t) : 0;
    if (d <= 7) buckets['0–7d']!++;
    else if (d <= 14) buckets['8–14d']!++;
    else if (d <= 30) buckets['15–30d']!++;
    else buckets['30d+']!++;
  });

  const kpi = (ic: string, color: string, val: number, lbl: string, sub: string) => (
    <div className="kpi" key={lbl}>
      <i className="material-icons ic" style={{ color }}>
        {ic}
      </i>
      <div>
        <div className="lbl">{lbl}</div>
        <div className="val">{val}</div>
        <div className="sub">{sub}</div>
      </div>
    </div>
  );

  const typeInCatalog = types.some((x) => x.name === draft.action_type);

  return (
    <div className="pm-ar">
      <div className="ar-head">
        <div className="ar-titleblock">
          <i className="material-icons ar-titleicon">checklist</i>
          <div>
            <h1 className="page-header__title">Action Register</h1>
            <p>
              Centralized tracking of actions, follow-ups, decisions, approvals and
              dependencies — with ownership and due dates.
            </p>
          </div>
          <button
            type="button"
            className="ar-new"
            disabled={!projectId}
            onClick={() => void openDrawer(null)}
          >
            <i className="material-icons" style={{ fontSize: 20 }}>
              add
            </i>{' '}
            New action
          </button>
        </div>

        <div className="ar-toolbar">
          <SelectField
            label="Project"
            width="lg"
            id="ar-project"
            value={projectId}
            onChange={(v) => void onProject(v)}
            placeholder={PROMPT.project}
            options={projects.map((p) => ({
              value: p.project_id,
              id: p.project_id,
                description: p.project_description,
            }))}
          />
          <SelectField
            label="Opportunity"
            width="lg"
            id="ar-opp"
            disabled={!projectId}
            value={oppId}
            onChange={(v) => {
              setOppId(v);
              void loadActions(projectId, v);
            }}
            placeholder={PROMPT.opportunity}
            options={[
              // Both of these are real choices, not placeholders — kept exactly as they were.
              { value: '', label: 'All (project-wide)' },
              { value: '__project__', label: 'Project-level only' },
              ...opps.map((o) => ({
                value: o.opportunity_id,
                id: o.opportunity_id,
                description: o.description,
              })),
            ]}
          />
          <div className="ar-fg ar-chkfg">
            <input
              type="checkbox"
              id="ar-openonly"
              checked={openOnly}
              onChange={(e) => setOpenOnly(e.target.checked)}
            />
            <label htmlFor="ar-openonly">Open items only</label>
          </div>
        </div>

        {/* The panel follows the screen's own tabs — pane 0 is Register, pane 1 is Dashboard. */}
        <HelpPanel helps={AR_HELPS.map((h) => withFinalizedRule(h))} activeIndex={tab === 'dashboard' ? 1 : 0} />

        <div className="ar-tabs">
          <button
            type="button"
            className={`rdtab${tab === 'register' ? ' active' : ''}`}
            onClick={() => setTab('register')}
          >
            <i className="material-icons" style={{ fontSize: 17 }}>
              list_alt
            </i>{' '}
            Register <span className="tcount">{list.length}</span>
          </button>
          <button
            type="button"
            className={`rdtab${tab === 'dashboard' ? ' active' : ''}`}
            onClick={() => setTab('dashboard')}
          >
            <i className="material-icons" style={{ fontSize: 17 }}>
              insights
            </i>{' '}
            Dashboard
          </button>
        </div>
      </div>

      <div className="ar-body">
        {tab === 'register' ? (
          !projectId ? (
            <div className="ar-empty">Select a project to view and track its actions.</div>
          ) : !list.length ? (
            <div className="ar-empty">
              {openOnly && actions.length ? (
                <>
                  No open actions for this scope. Untick <strong>Open items only</strong> to see
                  closed items.
                </>
              ) : (
                <>
                  No actions yet for this scope. Click <strong>+ New action</strong> to add one.
                </>
              )}
            </div>
          ) : (
            <div className="ar-list">
              <div className="ar-row head">
                <div />
                <div>Title</div>
                <div>Type</div>
                <div>Owner / Scope</div>
                <div>Due</div>
                <div>Status</div>
                <div>% </div>
                <div />
              </div>
              {list.map((a) => {
                const pc = PRIORITY_COLORS[a.priority ?? ''] || '#90a4ae';
                const sc = STATUS_COLORS[a.status ?? ''] || '#90a4ae';
                const over = !!(isOpen(a) && a.due_date && a.due_date.slice(0, 10) < t);
                const statusTxt =
                  (a.status ?? '') +
                  (a.status === 'Waiting' && a.waiting_on ? ` · ${a.waiting_on}` : '');
                return (
                  <div className="ar-row" key={a.id} onClick={() => void openDrawer(a)}>
                    <div>
                      <span
                        className="pri-dot"
                        style={{ background: pc }}
                        title={`${a.priority} priority`}
                      />
                    </div>
                    <div className="ar-title-cell">
                      <div className="t">{a.title}</div>
                      {a.description ? <div className="sub">{a.description}</div> : null}
                    </div>
                    <div>
                      {a.action_type ? (
                        <span className="chip" style={chipStyle('#3949ab')}>
                          {a.action_type}
                        </span>
                      ) : (
                        <span style={{ color: '#b0bec5' }}>—</span>
                      )}
                    </div>
                    <div className="ar-owner">
                      {a.owner || '—'}
                      <div style={{ fontSize: 11, color: '#90a4ae' }}>{scopeLabel(a)}</div>
                    </div>
                    <div className={`ar-due${over ? ' over' : ''}`}>{fmtDate(a.due_date)}</div>
                    <div>
                      <span className="chip" style={chipStyle(sc)}>
                        {statusTxt}
                      </span>
                    </div>
                    <div>
                      <div className="pct-wrap">
                        <div className="pct-bar" style={{ width: `${a.percent_complete || 0}%` }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#90a4ae', marginTop: 2 }}>
                        {a.percent_complete || 0}%
                      </div>
                    </div>
                    <div>
                      {/*
                        ON THE ROW as well as in the drawer, matching Portfolio Exceptions, so
                        you can mail someone without opening the action first.
                        `stopPropagation` keeps the row's own click — which opens the drawer —
                        from firing as well.
                      */}
                      <a
                        className="ar-fup"
                        href={followUpHref(a, directory)}
                        onClick={(e) => e.stopPropagation()}
                        title={
                          directory[String(a.owner ?? '').trim().toLowerCase()] ??
                          'No address found for this owner — the mail opens unaddressed'
                        }
                      >
                        Follow up
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : !projectId ? (
          <div className="ar-empty">Select a project to see the action dashboard.</div>
        ) : (
          <>
            <div className="kpi-grid">
              {kpi('pending_actions', '#3949ab', open.length, 'Total open', 'not completed/cancelled')}
              {kpi('event_busy', '#c62828', overdue.length, 'Overdue', 'past due date')}
              {kpi('date_range', '#ef6c00', dueWeek.length, 'Due this week', 'next 7 days')}
              {kpi('priority_high', '#d81b60', highPri.length, 'High priority', 'Critical + High')}
              {kpi('task_alt', '#2e7d32', closedMonth.length, 'Closed this month', monPrefix)}
              {kpi('timelapse', '#00897b', avgClose, 'Avg days to close', 'across closed items')}
            </div>

            <div className="chart-grid">
              <div className="chart-card">
                <h4>Open by owner</h4>
                <div className="chart-box">
                  <BarChart data={byOwner} color="#3949ab" />
                </div>
              </div>
              <div className="chart-card">
                <h4>By status</h4>
                <div className="chart-box">
                  <Doughnut slices={byStatus} />
                </div>
              </div>
              <div className="chart-card">
                <h4>By priority</h4>
                <div className="chart-box">
                  <Doughnut slices={byPri} />
                </div>
              </div>
              <div className="chart-card">
                <h4>Open by workstream</h4>
                <div className="chart-box">
                  <BarChart data={byWs} color="#00897b" />
                </div>
              </div>
              <div className="chart-card">
                <h4>Open item aging</h4>
                <div className="chart-box">
                  <BarChart data={buckets} color="#ef6c00" />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail / edit drawer */}
      <div
        className={`pm-ar-ovl${drawerOpen ? ' on' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />
      <aside
        className={`pm-ar-drawer${drawerOpen ? ' on' : ''}${editing ? ' editing' : ''}`}
        role="dialog"
        aria-label="Action detail"
      >
        <div className="ar-dhead">
          <span className="did">{draft.id ? `ACT-${draft.id}` : 'New action'}</span>
          {draft.id ? (
            <span className="chip" style={chipStyle(STATUS_COLORS[draft.status] || '#90a4ae')}>
              {draft.status}
            </span>
          ) : null}
          <div className="dsp">
            {draft.id ? (
              <button
                type="button"
                className="ar-dbtn del"
                title="Delete"
                onClick={() => void deleteAction()}
              >
                Delete
              </button>
            ) : null}
            {/*
              §13.4 — THE DRAWER OPENS IN EDIT MODE, always. Clicking a row means you intend to
              change it, so making you press Edit first is a step that buys nothing.

              The Edit button is GONE rather than hidden: with `editing` always true it would
              have nothing to do. And Cancel now REVERTS AND CLOSES — there is no read-only
              state left to fall back into, so a Cancel that only reverted would look like it
              had done nothing at all.
            */}
            <button
              type="button"
              className="ar-dbtn cancel"
              onClick={() => {
                if (snapshot) setDraft(snapshot);
                setDrawerOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ar-dbtn save"
              disabled={saving}
              onClick={() => void saveAction()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="ar-dx"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="ar-dbody">
          {notice ? (
            <div style={{ color: '#c62828', fontWeight: 600, fontSize: 12.5, marginBottom: 10 }}>
              {notice}
            </div>
          ) : null}

          <div className="ar-sec">
            <h3>
              <i className="material-icons">description</i> General
            </h3>
            <div className="fld-grid">
              <div className="fld full">
                <label>
                  Title <span className="req">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Short, action-oriented title"
                  value={draft.title}
                  onChange={(e) => set('title', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Type</label>
                <select
                  value={draft.action_type}
                  onChange={(e) => set('action_type', e.target.value)}
                >
                  <option value="">— Select —</option>
                  {/* Keep a current value even if it isn't in the active catalog. */}
                  {draft.action_type && !typeInCatalog ? (
                    <option value={draft.action_type}>{draft.action_type}</option>
                  ) : null}
                  {types.map((ty) => (
                    <option key={ty.name} value={ty.name}>
                      {ty.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label>Category</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={draft.category}
                  onChange={(e) => set('category', e.target.value)}
                />
              </div>
              <div className="fld full">
                <label>Description</label>
                <textarea
                  placeholder="What needs to happen and why"
                  value={draft.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              </div>
              {/*
                §5.3 — the Project field. The obvious move is to DISABLE "New action" when no
                single project is selected; the document is explicit that this is wrong, because
                the user will want it. So the drawer carries its own project instead.
              */}
              <div className="fld">
                <SelectField
                  label={
                    <>
                      Project <span style={{ color: '#c62828' }}>*</span>
                    </>
                  }
                  orientation="stacked"
                  width="lg"
                  placeholder={PROMPT.project}
                  /* Required: there is no "no project" action, so the prompt is not a
                     selectable row here. */
                  allowClear={false}
                  value={draft.project_id}
                  onChange={(pid) => {
                    set('project_id', pid);
                    /* CHANGING THE PROJECT RELOADS THE SCOPE LIST, and clears the current
                       selection with it. Opportunities belong to one project, so without this
                       an action could be attached to an initiative from a different one. */
                    set('opportunity_id', '');
                    void loadDrawerScope(pid);
                  }}
                  options={drawerProjects.map((p) => ({
                    value: p.project_id,
                    id: p.project_id,
                description: p.project_description,
                  }))}
                />
              </div>
              <div className="fld">
                <label>Scope</label>
                <select
                  value={draft.opportunity_id || '__project__'}
                  onChange={(e) => set('opportunity_id', e.target.value)}
                  disabled={!draft.project_id}
                >
                  <option value="__project__">Project-level (no opportunity)</option>
                  {drawerOpps.map((o) => (
                    <option key={o.opportunity_id} value={o.opportunity_id}>
                      {o.opportunity_id + (o.description ? ` — ${o.description}` : '')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label>Workstream</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={draft.workstream}
                  onChange={(e) => set('workstream', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Phase</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={draft.phase}
                  onChange={(e) => set('phase', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Sprint (if Agile)</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={draft.sprint}
                  onChange={(e) => set('sprint', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="ar-sec">
            <h3>
              <i className="material-icons">groups</i> Ownership
            </h3>
            <div className="fld-grid">
              <div className="fld">
                <label>Owner</label>
                <input
                  type="text"
                  placeholder="Accountable person"
                  value={draft.owner}
                  onChange={(e) => set('owner', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Assigned by</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={draft.assigned_by}
                  onChange={(e) => set('assigned_by', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Supporting team</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={draft.supporting_team}
                  onChange={(e) => set('supporting_team', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Stakeholders</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={draft.stakeholders}
                  onChange={(e) => set('stakeholders', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="ar-sec">
            <h3>
              <i className="material-icons">event</i> Dates
            </h3>
            <div className="fld-grid">
              <div className="fld">
                <label>Created date</label>
                <input
                  type="date"
                  value={draft.created_date}
                  onChange={(e) => set('created_date', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Due date</label>
                <input
                  type="date"
                  value={draft.due_date}
                  onChange={(e) => set('due_date', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Follow-up date</label>
                <input
                  type="date"
                  value={draft.followup_date}
                  onChange={(e) => set('followup_date', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Closed date</label>
                <input
                  type="date"
                  value={draft.closed_date}
                  onChange={(e) => set('closed_date', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="ar-sec">
            <h3>
              <i className="material-icons">flag</i> Status, Priority &amp; Progress
            </h3>
            <div className="fld-grid">
              <div className="fld">
                <label>Status</label>
                <select value={draft.status} onChange={(e) => set('status', e.target.value)}>
                  {STATUS_LIST.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fld">
                <label>Priority</label>
                <select value={draft.priority} onChange={(e) => set('priority', e.target.value)}>
                  {PRIORITY_LIST.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              {/* Waiting on appears only while Status is Waiting — the source's onStatusChange. */}
              {draft.status === 'Waiting' ? (
                <div className="fld">
                  <label>Waiting on</label>
                  <select
                    value={draft.waiting_on}
                    onChange={(e) => set('waiting_on', e.target.value)}
                  >
                    <option value="">—</option>
                    <option>Customer</option>
                    <option>Vendor</option>
                    <option>Internal</option>
                    <option>Other</option>
                  </select>
                </div>
              ) : null}
              <div className="fld">
                <label>% Complete</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  placeholder="0"
                  value={draft.percent_complete}
                  onChange={(e) => onPercentChange(e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Estimated effort</label>
                <input
                  type="text"
                  placeholder="e.g. 3 days"
                  value={draft.estimated_effort}
                  onChange={(e) => set('estimated_effort', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Actual effort</label>
                <input
                  type="text"
                  placeholder="e.g. 2 days"
                  value={draft.actual_effort}
                  onChange={(e) => set('actual_effort', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="ar-sec">
            <h3>
              <i className="material-icons">forum</i> Collaboration
            </h3>
            <div className="fld-grid">
              <div className="fld full">
                <label>Comments</label>
                <textarea
                  placeholder="Notes, updates, discussion"
                  value={draft.comments}
                  onChange={(e) => set('comments', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Attachments</label>
                <input
                  type="text"
                  placeholder="File name(s) or link"
                  value={draft.attachments}
                  onChange={(e) => set('attachments', e.target.value)}
                />
              </div>
              <div className="fld">
                <label>Meeting minutes link</label>
                <input
                  type="text"
                  placeholder="URL"
                  value={draft.meeting_minutes_link}
                  onChange={(e) => set('meeting_minutes_link', e.target.value)}
                />
              </div>
              <div className="fld full">
                <label>External links</label>
                <input
                  type="text"
                  placeholder="URL(s)"
                  value={draft.external_links}
                  onChange={(e) => set('external_links', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
